import { Dialog, Button, DialogContent, DialogTitle, DriftIcons } from 'drift-ui';
import React, { FC, Dispatch, SetStateAction, useMemo } from 'react';
import css from './Menu.scss';
import { useTranslation } from 'react-i18next';
import { useApplication } from 'hooks/useApplication';
import {
  DO_APPLICATION_TYPE,
  formApplicationSections,
  SectionInformation,
  sectionMetadata,
} from 'client-experience-toolbox';
import { useDispatch } from 'react-redux';
import { SET_BANKING_SECTION } from 'app/actionTypes';
const { KeyboardArrowRight, CheckCircleOutlined } = DriftIcons;
import cx from 'classnames';
import { shouldSkipSection } from 'utils/useFormNetworkCalls';

type MenuProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  type: DO_APPLICATION_TYPE;
};

type ApplicationSectionProps = {
  showCompleted: boolean;
  translationText: string;
  section: number;
  inaccessible: boolean;
  type: DO_APPLICATION_TYPE;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const ApplicationSection: FC<ApplicationSectionProps> = ({
  showCompleted,
  translationText,
  section,
  inaccessible,
  type,
  setOpen,
}) => {
  const dispatch = useDispatch();

  const handleSectionClick = (section: number) => {
    if (type === DO_APPLICATION_TYPE.FACTORING) {
      dispatch({ type: 'SET_FACTORING_SECTION', factoringSection: section });
    } else if (type === DO_APPLICATION_TYPE.BANKING) {
      dispatch({ type: SET_BANKING_SECTION, bankingSection: section });
    }
    setOpen(false);
  };

  const displayCompleted = !inaccessible && showCompleted;

  return (
    <Button
      data-testid={`application-section-${section}`}
      className={css.menuSectionButton}
      onClick={() => handleSectionClick(section)}
      disabled={inaccessible}
    >
      <p className={!inaccessible ? css.menuSectionTitleBold : css.menuSectionTitle}>{translationText}</p>
      {displayCompleted ? (
        <CheckCircleOutlined fontSize="large" color="success" />
      ) : (
        <KeyboardArrowRight fontSize="large" color={!inaccessible ? 'inherit' : 'disabled'} />
      )}
    </Button>
  );
};

const getSectionCompletion = (
  applicationType: DO_APPLICATION_TYPE,
  sectionsCompletion?: { [key: string]: { completedAt?: number | null } },
) => {
  const baseStatuses = formApplicationSections(applicationType);
  return baseStatuses.reduce<Record<string, boolean>>((acc, status) => {
    Reflect.set(acc, status, !!sectionsCompletion?.[status]?.completedAt);
    return acc;
  }, {});
};

export const Menu: FC<MenuProps> = ({ open, setOpen, type }) => {
  const { application } = useApplication(type);
  const { t } = useTranslation();
  const sectionsCompletion = application?.sectionsCompletion;

  const sections = useMemo(() => {
    let hasFoundIncompleteSection = false;
    const sectionStatuses = getSectionCompletion(type, sectionsCompletion);
    const sectionsArray: Array<SectionInformation> = [];

    Object.entries(sectionStatuses).forEach(([sectionName, isCompleted]) => {
      if (Object.keys(sectionMetadata[type]).includes(sectionName)) {
        const { pageNumber, title } = sectionMetadata[type][sectionName];
        const titleArgs: Record<string, any> = {};
        const isSkippedSection = shouldSkipSection(application, sectionName);

        const sectionInfo: SectionInformation = {
          pageNumber,
          title: title(titleArgs),
          completed: isCompleted,
          inaccessible: hasFoundIncompleteSection || isSkippedSection,
        };

        sectionsArray.push(sectionInfo);
        if (!isCompleted && !isSkippedSection) hasFoundIncompleteSection = true;
      }
    });
    return sectionsArray;
  }, [sectionsCompletion, type]);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="menu-modal-title"
      aria-describedby="menu-modal-description"
    >
      <DialogTitle className={css.menuTitle} id="menu-modal-title" data-testid="menu-modal-title">
        {t('Menu')}
      </DialogTitle>
      <DialogContent className={css.menuContainer}>
        {sections.map((sectionInfo, index) => (
          <div
            key={sectionInfo.title}
            className={cx(css.menuSectionContent, index === sections.length - 1 && css.menuWithDivider)}
          >
            <ApplicationSection
              showCompleted={!!sectionInfo.completed}
              translationText={t(`${sectionInfo.title}`)}
              section={sectionInfo.pageNumber + 1}
              inaccessible={sectionInfo.inaccessible}
              type={type}
              setOpen={setOpen}
            />
          </div>
        ))}
        <div className={css.menuButtonContainer}>
          <Button size="large" variant="contained" data-testid="menu-close-button" onClick={() => setOpen(false)}>
            {t('Close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
